<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="mb-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="modal"
              data-bs-target="#add_asset"
              class="btn btn-primary d-flex align-items-center"
              v-can="'Asset.Create'"
            >
              <i class="ti ti-circle-plus me-2"></i>Add Asset
            </a>
          </div>
          <div class="ms-2 head-icons">
            <a href="javascript:void(0);" id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="row g-2 align-items-end">
            <div class="col-md-3">
              <label class="form-label">Search (name, tag, serial)</label>
              <input v-model="assetStore.filters.search" class="form-control" placeholder="e.g. Dell, TAG-1001, SN123" />
            </div>
            <div class="col-md-3">
              <label class="form-label">Category</label>
              <select v-model="assetStore.filters.category_id" class="form-select">
                <option value="">All</option>
                <option v-for="c in categoryStore.items" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label">Assigned To</label>
              <select v-model="assetStore.filters.assigned_to_id" class="form-select">
                <option value="">Anyone</option>
                <option v-for="u in userStore.users" :key="u.id" :value="u.id">
                  {{ u.first_name }} {{ u.last_name }}
                </option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label">Status</label>
              <select v-model="assetStore.filters.status" class="form-select">
                <option value="">All</option>
                <option>Active</option>
                <option>Inactive</option>
                <option>Disposed</option>
                <option>Maintenance</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label">Purchased (From - To)</label>
              <div class="d-flex gap-2">
                <input v-model="assetStore.filters.purchased_from" type="date" class="form-control" />
                <input v-model="assetStore.filters.purchased_to" type="date" class="form-control" />
              </div>
            </div>
            <div class="col-md-2 d-grid">
              <button class="btn btn-primary" @click="assetStore.fetchAll({ page: 1 })">Filter</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <h5>Assets List</h5>
        </div>
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <assets-table
              :rows="assetStore.items"
              :loading="assetStore.loading"
              @edit="openEdit"
              @delete="confirmDelete"
              @bulk-delete="confirmBulkDelete"
            />
          </div>

          <div class="p-3 d-flex justify-content-between align-items-center">
            <div>Showing page {{ assetStore.pagination.current_page }} of {{ totalPages }}</div>
            <div class="btn-group">
              <button
                class="btn btn-outline-secondary"
                :disabled="assetStore.pagination.current_page <= 1"
                @click="assetStore.fetchAll({ page: assetStore.pagination.current_page - 1 })"
              >Prev</button>
              <button
                class="btn btn-outline-secondary"
                :disabled="assetStore.pagination.current_page >= totalPages"
                @click="assetStore.fetchAll({ page: assetStore.pagination.current_page + 1 })"
              >Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>

  <asset-modal :edit-item="editItem" @saved="onSaved" />
</template>

<script setup>
import { computed, onMounted, ref, nextTick } from "vue";
import Modal from "bootstrap/js/dist/modal";
import { useAssetStore } from "@/stores/assetStore";
import { useAssetCategoryStore } from "@/stores/assetCategoryStore";
import { useUserStore } from "@/stores/userStore";
import AssetsTable from "./assets-table.vue";
import AssetModal from "@/components/modal/assets-modal.vue";

const title = "Assets";
const text = "Administration";
const text1 = "Assets";

const assetStore = useAssetStore();
const categoryStore = useAssetCategoryStore();
const userStore = useUserStore();

const editItem = ref(null);
let editModal, deleteModal;

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}
function getOrCreateModal(el) {
  if (!el) return null;
  return Modal.getOrCreateInstance(el, { backdrop: true, keyboard: true });
}

const totalPages = computed(() => {
  const t = assetStore.pagination.total || 0;
  const p = assetStore.pagination.per_page || 25;
  return Math.max(1, Math.ceil(t / p));
});

function openEdit(row) {
  editItem.value = row;
  nextTick(() => {
    const el = document.getElementById("edit_asset");
    editModal = getOrCreateModal(el);
    editModal?.show();
  });
}

async function confirmDelete(row) {
  editItem.value = row;
  await nextTick();
  const el = document.getElementById("delete_modal");
  deleteModal = getOrCreateModal(el);
  deleteModal?.show();
}

async function confirmBulkDelete(ids) {
  if (!ids?.length) return;
  if (!confirm(`Delete ${ids.length} selected item(s)?`)) return;
  await assetStore.bulkDelete(ids);
}

function onSaved() {
  editItem.value = null;
}

onMounted(async () => {
  await Promise.all([
    assetStore.fetchAll(),
    categoryStore.fetchAll({ per_page: 500 }), // for dropdown
    userStore.fetchUsers({ per_page: 500 }),   // populate "Assigned To" options
  ]);

  const editEl = document.getElementById("edit_asset");
  editEl?.addEventListener("hidden.bs.modal", () => (editItem.value = null));
});
</script>


